import React, { useState, useEffect } from "react";
import { Link } from "gatsby";
import { ChevronDown } from "lucide-react";
import { NavbarProps, MenuItem } from "@/types/navbar";
import { defaultNavbarContent } from "./defaultNavbarContent";
import { LinkWrapper } from "@/components/LinkWrapper";
import { PosterBoldPillButton } from "@/components/posterBold/PosterBoldPillButton";
import HamburgerButton from "./shared/HamburgerButtons";
import { getMenuItems } from "./shared/menuItems";
import { useEscapeKey } from "./shared/useEscapeKey";
import { FocusScope } from "@radix-ui/react-focus-scope";

const NavBarPosterBold: React.FC<Partial<NavbarProps>> = ({
  services = [],
  primaryCTA = defaultNavbarContent.primaryCTA,
  primaryCTAUrl = defaultNavbarContent.primaryCTAUrl,
  logoUrl = defaultNavbarContent.logoUrl,
  enableResources = true,
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuItems = getMenuItems(services, enableResources);

  // Disable scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileOpen]);

  return (
    <header className="bg-primary text-primary-foreground">
      <div className="container mx-auto flex items-center justify-between px-6 py-5">
        {/* Logo */}
        <Link to="/" className="flex items-center" aria-label="Home">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="h-8 w-auto" />
          ) : (
            <span className="font-display text-xl font-black text-primary-foreground">
              LOGO
            </span>
          )}
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-2 md:flex">
          {menuItems.map(item =>
            item.isDropdown && item.dropdownItems && item.dropdownItems.length > 0 ? (
              // Parent is a real link to the overview page; the submenu opens on
              // hover and on keyboard focus (focus-within), holding real links.
              <div key={item.label} className="group relative">
                <LinkWrapper
                  to={item.href}
                  className="inline-flex h-9 items-center gap-1 px-2 text-sm font-semibold text-primary-foreground hover:underline lg:px-4"
                >
                  {item.label}
                  <ChevronDown className="h-3 w-3" aria-hidden="true" />
                </LinkWrapper>
                <div className="invisible absolute left-0 top-full z-50 min-w-[220px] border-2 border-foreground bg-background group-hover:visible group-focus-within:visible">
                  <ul className="py-2">
                    {item.dropdownItems.map(dropdownItem => (
                      <li key={dropdownItem.slug}>
                        <LinkWrapper
                          to={dropdownItem.href}
                          className="block px-5 py-2 text-sm font-semibold text-foreground hover:bg-primary hover:text-primary-foreground"
                        >
                          {dropdownItem.name}
                        </LinkWrapper>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <LinkWrapper
                key={item.label}
                to={item.href}
                className="inline-flex h-9 items-center px-2 text-sm font-semibold text-primary-foreground hover:underline lg:px-4"
              >
                {item.label}
              </LinkWrapper>
            )
          )}

          {primaryCTA && primaryCTAUrl && (
            <PosterBoldPillButton href={primaryCTAUrl} tone="onPrimary" className="ml-2 lg:ml-4">
              {primaryCTA}
            </PosterBoldPillButton>
          )}
        </nav>

        {/* Hamburger button — mobile only */}
        <div className="flex items-center md:hidden">
          <HamburgerButton
            isOpen={mobileOpen}
            toggleMenu={() => setMobileOpen(v => !v)}
          />
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <MobileMenu
          menuItems={menuItems}
          setMobileOpen={setMobileOpen}
          primaryCTA={primaryCTA}
          primaryCTAUrl={primaryCTAUrl}
        />
      )}
    </header>
  );
};

export default NavBarPosterBold;

interface MobileMenuProps {
  menuItems: MenuItem[];
  setMobileOpen: (value: boolean | ((prev: boolean) => boolean)) => void;
  primaryCTA?: string;
  primaryCTAUrl?: string;
}

function MobileMenu({
  menuItems,
  setMobileOpen,
  primaryCTA,
  primaryCTAUrl,
}: MobileMenuProps) {
  useEscapeKey(() => setMobileOpen(false));

  return (
    <FocusScope trapped>
    <div className="border-t-2 border-primary-foreground bg-primary text-primary-foreground md:hidden">
      <nav className="container mx-auto flex flex-col gap-4 px-6 py-6">
        {menuItems.map(item =>
          item.isDropdown && item.dropdownItems && item.dropdownItems.length > 0 ? (
            <div key={item.label} className="flex flex-col">
              <span className="text-sm font-semibold text-primary-foreground">
                {item.label}
              </span>
              <div className="mt-2 flex flex-col gap-2 pl-4">
                {item.dropdownItems.map(dropdownItem => (
                  <Link
                    key={dropdownItem.slug}
                    to={dropdownItem.href}
                    className="text-sm font-semibold text-primary-foreground/80 hover:text-primary-foreground hover:underline"
                    onClick={() => setMobileOpen(false)}
                  >
                    {dropdownItem.name}
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            <Link
              key={item.label}
              to={item.href}
              className="text-sm font-semibold text-primary-foreground hover:underline"
              onClick={() => setMobileOpen(false)}
            >
              {item.label}
            </Link>
          )
        )}
      </nav>
      {primaryCTA && primaryCTAUrl && (
        <div className="container mx-auto px-6 pb-6">
          <PosterBoldPillButton
            href={primaryCTAUrl}
            tone="onPrimary"
            className="w-full justify-center"
            onClick={() => setMobileOpen(false)}
          >
            {primaryCTA}
          </PosterBoldPillButton>
        </div>
      )}
    </div>
    </FocusScope>
  );
}
